import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateTime } from "../dates";
import { alphaMutationRejected } from "../alpha-mutation";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router";
import { api } from "../api";
import { ErrorState, Loading } from "../ui/State";
export function WarrantiesAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [model, setModel] = useState<any>(),
    [events, setEvents] = useState<any[]>([]),
    [selected, setSelected] = useState(""),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const pending = useRef<{ url: string; body: any } | null>(null);
  const load = () =>
    building
      ? api("/api/alpha/warranties?building=" + building).then(setModel)
      : Promise.resolve();
  useEffect(() => {
    setModel(undefined);
    setSelected("");
    setEvents([]);
    void load().catch(setError);
  }, [building]);
  useEffect(() => {
    setEvents([]);
    let active = true;
    if (selected)
      api<any>("/api/alpha/warranties/" + selected)
        .then((r) => {
          if (active) setEvents(r.events);
        })
        .catch(setError);
    return () => {
      active = false;
    };
  }, [selected]);
  async function save(url: string, body: any) {
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      const r = await api<any>(pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setError(undefined);
      await load();
      setSelected(r.id);
      setEvents((await api<any>("/api/alpha/warranties/" + r.id)).events);
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Warranty and defect recovery</h1>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void save("", {})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Choose a building.</p>
      ) : !model ? (
        <Loading />
      ) : (
        <>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save("/api/alpha/warranties", {
                workId: f.get("work"),
                supplierId: f.get("supplier"),
                documentId: f.get("document"),
                defect: f.get("defect"),
                dueDate: f.get("due"),
              });
            }}
          >
            <fieldset disabled={busy || !!pending.current}>
              <legend>Prepare a recovery</legend>
              <label>
                Original work
                <select name="work" required>
                  <option value="">Choose work</option>
                  {model.work.map((w: any) => (
                    <option key={w.id} value={w.id}>
                      {w.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Responsible supplier
                <select name="supplier" required>
                  <option value="">Choose supplier</option>
                  {model.suppliers.map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {s.legal_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Warranty or contractual evidence
                <select name="document" required>
                  <option value="">Choose evidence</option>
                  {model.documents.map((d: any) => (
                    <option key={d.id} value={d.id}>
                      {d.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Defect and remedy sought
                <textarea
                  name="defect"
                  required
                  minLength={10}
                  maxLength={5000}
                />
              </label>
              <label>
                Follow up by
                <DateOnlyInput name="due" required />
              </label>
              <button>Prepare recovery</button>
            </fieldset>
          </form>
          <h2>Recoveries</h2>
          {model.recoveries.length === 0 && <p>No recoveries prepared.</p>}
          {model.recoveries.map((r: any) => (
            <section className="panel" key={r.id}>
              <h3>
                <button onClick={() => setSelected(r.id)}>{r.title}</button>
              </h3>
              <p>
                {r.legal_name} · {r.status}
              </p>
              <p>{r.defect}</p>
              <Link to={"/work/actions/" + r.action_id}>
                Open remedy and verification
              </Link>
            </section>
          ))}
          {selected && (
            <section className="panel">
              <h2>Recovery progress</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  void save("/api/alpha/warranties/" + selected + "/events", {
                    outcome: f.get("outcome"),
                    note: f.get("note"),
                  });
                }}
              >
                <fieldset disabled={busy || !!pending.current}>
                  <legend>Add progress</legend>
                  <label>
                    Outcome
                    <select name="outcome">
                      {[
                        "Supplier contacted",
                        "Supplier accepted",
                        "Supplier disputed",
                        "Remedy reported",
                        "Follow-up",
                      ].map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Details
                    <textarea
                      name="note"
                      required
                      minLength={5}
                      maxLength={5000}
                    />
                  </label>
                  <p>Reporting a remedy does not verify or close the Action.</p>
                  <button>Save progress</button>
                </fieldset>
              </form>
              {events.map((e) => (
                <article key={e.id}>
                  <h3>{e.outcome}</h3>
                  <p>{e.note}</p>
                  <small>{formatDateTime(e.created_at)}</small>
                </article>
              ))}
            </section>
          )}
        </>
      )}
    </>
  );
}
